import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { DailyTable } from "@/features/daily/components/DailyTable";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";
import { floatingPanel, type FloatingPanelPosition } from "@/features/daily/components/command/floating-panel";
import type { TodayHourVm, TodayHourlyVm } from "@/features/daily/components/command/today-view-model";

const GRID = { left: 8, right: 8, top: 8, bottom: 8 };
const DETAIL_WIDTH = 248;
const DETAIL_HEIGHT = 310;
const DETAIL_ID = "daily-hourly-detail";

type Hovered = { slot: number; position: FloatingPanelPosition };

type PlotBox = { left: number; width: number; height: number };

const samePlot = (left: PlotBox | null, right: PlotBox): boolean =>
    left !== null && left.left === right.left && left.width === right.width && left.height === right.height;

const expectedText = (expected: number | null): string => (expected === null ? dailyFormat.absent : dailyFormat.exactCount(Math.round(expected)));

const HourDetail: React.FC<{ hour: TodayHourVm; position: FloatingPanelPosition }> = ({ hour, position }) => (
    <div id={DETAIL_ID} role="tooltip" className="daily-hourly__detail" style={{ left: position.left, top: position.top, width: DETAIL_WIDTH }}>
        <div className="daily-hourly__detail-head">
            <span className="daily-hourly__detail-range">{hour.range}</span>
            {hour.tag === null ? null : <span className="daily-hourly__detail-tag">{hour.tag}</span>}
        </div>
        <div className="daily-hourly__detail-block">
            <span className="daily-hourly__detail-title">Creadas en esta hora</span>
            <div className="daily-hourly__detail-rows">
                <span className="daily-cc-legend__bar" data-series="orders" aria-hidden="true" /><span>Órdenes</span><b>{dailyFormat.exactCount(hour.orders)}</b>
                <span className="daily-cc-legend__bar" data-series="deposits" aria-hidden="true" /><span>Depósitos</span><b>{dailyFormat.exactCount(hour.deposits)}</b>
                <span aria-hidden="true" /><span className="daily-hourly__detail-total">Total creadas</span><b>{dailyFormat.exactCount(hour.created)}</b>
            </div>
        </div>
        <div className="daily-hourly__detail-block daily-hourly__detail-block--split">
            <span className="daily-hourly__detail-title">Completadas vs. esperadas</span>
            <span className="daily-hourly__detail-sub">esperadas: completadas medias del mismo tramo</span>
            <div className="daily-hourly__detail-rows">
                <span className="daily-cc-legend__line" data-series="completed" aria-hidden="true" /><span>Completadas</span><b>{dailyFormat.exactCount(hour.completed)}</b>
                <span className="daily-cc-legend__line" data-series="expected" aria-hidden="true" /><span>{hour.completedExpectedLabel}</span><b>{dailyFormat.exactCount(hour.completedExpected)}</b>
            </div>
            {hour.delta === null ? null : <div className="daily-hourly__detail-delta" data-tone={hour.delta.tone}>{hour.delta.text}</div>}
        </div>
        {hour.footnotes.map((note) => <div key={note} className="daily-hourly__detail-foot">{note}</div>)}
    </div>
);

type Palette = { blue: string; violet: string; green: string; muted: string; soft: string; surface: string; grid: string; text: string; fontSize: number };

/** Canvas needs literal colours: read the page's own tokens off the chart's box. */
const readPalette = (node: HTMLElement): Palette => {
    const style = getComputedStyle(node);
    const token = (name: string): string => style.getPropertyValue(name).trim();
    const fontSize = Number.parseFloat(token("--daily-text-xs"));
    return {
        blue: token("--daily-blue"),
        violet: token("--daily-violet"),
        green: token("--daily-green"),
        muted: token("--daily-muted"),
        soft: token("--daily-soft"),
        surface: token("--daily-surface"),
        grid: token("--daily-line"),
        text: token("--daily-muted"),
        fontSize: Number.isNaN(fontSize) ? 11 : fontSize,
    };
};

/**
 * D134 + D136: today hour by hour — created orders and deposits as stacked bars (blue and
 * violet, validated as a categorical pair), completed operations as a line, the baseline's
 * expectation dashed, quiet slots shaded. Everything drawn is in the table for screen
 * readers (WCAG 1.1.1); the canvas fills its box and follows it (D132).
 */
export const TodayHourlyCard: React.FC<{ vm: TodayHourlyVm }> = ({ vm }) => {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const [palette, setPalette] = useState<Palette | null>(null);
    const [hovered, setHovered] = useState<Hovered | null>(null);
    const echartsRef = useRef<ReactECharts | null>(null);
    const [plot, setPlot] = useState<PlotBox | null>(null);
    // The hour buttons sit over the canvas, on the bands the chart itself drew (its label area is measured, not guessed).
    const measure = useCallback((): void => {
        const chart = echartsRef.current;
        const first = vm.hours[0];
        const last = vm.hours[vm.hours.length - 1];
        if (chart === null || first === undefined || last === undefined) return;
        const instance = chart.getEchartsInstance();
        const start = instance.convertToPixel({ xAxisIndex: 0 }, first.label);
        const end = instance.convertToPixel({ xAxisIndex: 0 }, last.label);
        const band = vm.hours.length > 1 ? (end - start) / (vm.hours.length - 1) : 2 * (instance.getWidth() - GRID.right - start);
        const next: PlotBox = { left: start - band / 2, width: band * vm.hours.length, height: instance.convertToPixel({ yAxisIndex: 0 }, 0) };
        if ([next.left, next.width, next.height].every((value) => Number.isFinite(value))) setPlot((current) => (samePlot(current, next) ? current : next));
    }, [vm.hours]);
    const events = useMemo(() => ({ finished: measure }), [measure]);
    const show = (event: React.SyntheticEvent<HTMLElement>, index: number): void => {
        const side = index < vm.hours.length / 2 ? "right" : "left";
        setHovered({ slot: index, position: floatingPanel.place(event.currentTarget.getBoundingClientRect(), DETAIL_WIDTH, DETAIL_HEIGHT, side) });
    };
    const hide = (): void => setHovered(null);
    const active = hovered === null ? undefined : vm.hours.find((hour) => hour.slot === hovered.slot);

    useEffect(() => {
        const node = chartRef.current;
        if (node === null) return undefined;
        const update = (): void => setPalette(readPalette(node));
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    const option = useMemo(() => {
        if (palette === null) return null;
        const axisLabel = { color: palette.text, fontSize: palette.fontSize };
        const labels = vm.hours.map((hour) => hour.label);
        const quietArea = {
            silent: true,
            itemStyle: { color: palette.soft },
            data: vm.hours.filter((hour) => hour.quiet).map((hour) => [{ xAxis: hour.label }, { xAxis: hour.label }]),
        };
        return {
            animation: false,
            grid: { ...GRID, containLabel: true },
            legend: { show: false },
            tooltip: { show: false },
            xAxis: { type: "category", data: labels, axisLabel, axisLine: { lineStyle: { color: palette.grid } }, axisTick: { show: false } },
            yAxis: { type: "value", minInterval: 1, axisLabel, splitLine: { lineStyle: { color: palette.grid, type: "dashed" } } },
            series: [
                // D136: created split by type, stacked, a 1 px surface border between segments.
                // vb-metrics#38 U5: always by type, so the legend never changes its words.
                { name: "Órdenes creadas", type: "bar", stack: "created", data: vm.hours.map((hour) => hour.orders), itemStyle: { color: palette.blue, borderColor: palette.surface, borderWidth: 1 }, barMaxWidth: 18, markArea: quietArea },
                { name: "Depósitos creados", type: "bar", stack: "created", data: vm.hours.map((hour) => hour.deposits), itemStyle: { color: palette.violet, borderColor: palette.surface, borderWidth: 1, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 18 },
                { name: "Completadas", type: "line", data: vm.hours.map((hour) => hour.completed), showSymbol: false, lineStyle: { color: palette.green, width: 2 }, itemStyle: { color: palette.green } },
                ...(vm.baselineNote === null ? [{
                    name: "Esperadas",
                    type: "line",
                    data: vm.hours.map((hour) => hour.expected),
                    showSymbol: false,
                    connectNulls: false,
                    lineStyle: { color: palette.muted, width: 2, type: "dashed" },
                    itemStyle: { color: palette.muted },
                }] : []),
            ],
        };
    }, [palette, vm]);

    return (
        <section className="daily-panel daily-today-card daily-hourly" aria-labelledby="daily-hourly-title">
            <div className="daily-panel__head">
                <h2 id="daily-hourly-title">Actividad de hoy por hora</h2>
                <ul className="daily-cc-legend" aria-hidden="true">
                    <li><span className="daily-cc-legend__bar" data-series="orders" />Órdenes creadas</li>
                    <li><span className="daily-cc-legend__bar" data-series="deposits" />Depósitos creados</li>
                    <li><span className="daily-cc-legend__line" data-series="completed" />Completadas</li>
                    {vm.baselineNote === null ? <li><span className="daily-cc-legend__line" data-series="expected" />Esperadas</li> : null}
                </ul>
                {vm.baselineNote === null ? null : <span className="daily-small daily-muted">{vm.baselineNote}</span>}
            </div>
            <div className="daily-hourly__plot">
                <div ref={chartRef} className="daily-chart daily-hourly__chart" role="img" aria-label="Operaciones creadas y completadas por hora frente a lo esperado. Los datos están en la tabla.">
                    {option === null ? null : (
                        <ReactECharts ref={echartsRef} option={option} onEvents={events} notMerge autoResize style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                    )}
                </div>
                <div className="daily-hourly__hits" style={plot === null ? { inset: 0 } : { left: plot.left, top: 0, width: plot.width, height: plot.height }}>
                    {vm.hours.map((hour, index) => (
                        <button
                            key={hour.slot}
                            type="button"
                            className="daily-hourly__hit"
                            aria-label={hour.range}
                            aria-describedby={active !== undefined && active.slot === hour.slot ? DETAIL_ID : undefined}
                            onMouseEnter={(event) => show(event, index)}
                            onFocus={(event) => show(event, index)}
                            onMouseLeave={hide}
                            onBlur={hide}
                            onKeyDown={(event) => {
                                if (event.key === "Escape") hide();
                            }}
                        />
                    ))}
                </div>
            </div>
            {active === undefined || hovered === null ? null : <HourDetail hour={active} position={hovered.position} />}
            <div className="daily-visually-hidden">
                <DailyTable
                    caption="Actividad de hoy por hora"
                    columns={[
                        { key: "hour", header: "Hora", render: (hour: TodayHourVm) => `${hour.label}${hour.quiet ? " · tranquila" : ""}` },
                        { key: "orders", header: "Órdenes creadas", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.orders) },
                        { key: "deposits", header: "Depósitos creados", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.deposits) },
                        { key: "completed", header: "Completadas", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.completed) },
                        { key: "expected", header: "Esperadas", numeric: true, render: (hour: TodayHourVm) => expectedText(hour.expected) },
                    ]}
                    rows={vm.hours}
                    rowKey={(hour) => String(hour.slot)}
                    emptyMessage="Aún no ha transcurrido ninguna hora de hoy."
                />
            </div>
        </section>
    );
};
